import React, { useState, memo, useCallback, useEffect, useMemo } from 'react';
import Box from '@mui/material/Box';
import TableCell from '@mui/material/TableCell';
import IconButton from '@mui/material/IconButton';
import { ArrowDownMenuIcon, MenuVerticalIcon, TrashIcon } from '~/assets/icons';
import styles from './EstimateList.module.scss';
import classNames from 'classnames/bind';
import BaseTable from '~/components/common/base-table/BaseTable';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { HeadCelType, SortDirection } from '~/utils/types/common';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { catchError, formatCurrency, handleApiError, notify } from '~/utils/helper';
import { IEstimateList, IPayloadDeleteEstimates, IResponseGetEstimate } from '~/utils/interface/estimate';
import { Button, Menu, MenuItem } from '@mui/material';
import CreateEstimateDialog from './create-estimate-dialog/CreateEstimateDialog';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import usePermission from '~/hooks/usePermission';
import { PERMISSIONS } from '~/utils/constants/permission';
import { handleGetEstimate, handleGetEstimateList, handleRevokeDocument } from '~/thunks/estimate/estimateThunk';
import { estimateActions } from '~/thunks/estimate/estimateSlice';
import { IJob } from '~/utils/interface/job';
import ModalAlertDelete from '~/components/common/modal-alert-delete/ModalAlertDelete';
import estimateApi from '~/apis/estimate';
import CreateFromTemplateDialog from './create-from-template/CreateFromTemplateDialog';
import { handleGetEstimateTemplate } from '~/thunks/estimate-template/estimateTemplateThunk';
import MenuOption from '~/components/estimate/menu-option/MenuOption';
import SendDocumentDialog from '~/components/estimate/esign/send-document/SendDocumentDialog';

const cx = classNames.bind(styles);

type EstimateListProps = {
  id: string;
  data: IJob | undefined;
  updateData: () => void;
};

function EstimateList(props: EstimateListProps) {
  //#region Destructuring Props
  //#endregion Destructuring Props
  //#region Declare Hook
  const permissions = usePermission();
  const dispatch = useAppDispatch();
  const { t } = useTranslation('admin/job');
  const { profile } = useAppSelector((state) => state.auth);
  const [enableSign, setEnableSign] = useState<boolean>(false);
  const [enableRevoke, setEnableRevoke] = useState<boolean>(false);

  const initValues: IResponseGetEstimate = {
    id: 0,
    total: 0,
    subtotal: 0,
    tax: profile.tax ?? 0,
    term: '',
    openingStatement: '',
    estimateItems: [],
    estimateFiles: [],
  };
  const headCells: Array<HeadCelType> = useMemo(
    () => [
      {
        id: 'name',
        label: t(`properties.clientName`),
        sortable: true,
        searchable: true,
      },
      {
        id: 'total',
        label: t(`properties.totalAmount`),
        sortable: true,
        searchable: true,
      },
      {
        id: 'createdAt',
        label: t(`properties.createdAt`),
        sortable: true,
        searchable: true,
      },
      {
        id: 'action',
        label: t(`properties.action`),
        sortable: false,
        searchable: false,
      },
    ],
    [t]
  );
  //#endregion Declare Hook
  //#region Selector
  const { filterList, currentPage, totalPages, needRefreshData, openAlertDelete, menuSelectedId, selectedIds } =
    useAppSelector((state) => state.estimate);
  //#endregion Selector
  //#region Declare State
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [addEstimate, setAddEstimate] = useState<null | HTMLElement>(null);
  const [openCreateEstimateDialog, setOpenCreateEstimateDialog] = useState<boolean>(false);
  const [openCreateFromTemplate, setOpenCreateFromTemplate] = useState<boolean>(false);
  const [createEstimateDialogMode, setCreateEstimateDialogMode] = useState<string>('');
  const [selectId, setSelectId] = useState<string>('');
  const [estimateList, setEstimateList] = useState<Array<IEstimateList>>([]);
  const [editData, setEditData] = useState<IResponseGetEstimate>();

  const [openSenDocumentDialog, setOpenSendDocumentDialog] = useState<boolean>(false);
  //#endregion Declare State
  //#region Implement Hook
  useEffect(() => {
    const handleGetList = async () => {
      try {
        const response = await dispatch(handleGetEstimateList({ jobId: props.id, data: filterList })).unwrap();
        setEstimateList(response.responses);
      } catch (e) {
        handleApiError(e);
      }
    };
    handleGetList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.id, filterList, needRefreshData, dispatch]);

  const openOptionMenu = (
    event: React.MouseEvent<HTMLButtonElement>,
    itemId: string,
    signDocumentId?: string,
    signStatus?: string
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectId(itemId);
    if (!signDocumentId) setEnableSign(true);
    else setEnableSign(false);

    if (signStatus === 'Signature Needed') setEnableRevoke(true);
    else setEnableRevoke(false);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const setSelectedIds = useCallback((selected: Array<string>) => {
    dispatch(estimateActions.changeMenuSelectId(undefined));
    dispatch(estimateActions.changeSelectedIds(selected));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEditItem = async () => {
    handleClose();
    const response = await dispatch(handleGetEstimate(selectId)).unwrap();
    setEditData(response);
    setCreateEstimateDialogMode(DIALOG_MODE_EDIT);
    setOpenCreateEstimateDialog(true);
  };

  const handleDeleteItem = () => {
    handleClose();
    if (selectId !== undefined) {
      dispatch(estimateActions.changeMenuSelectId(selectId));
      dispatch(estimateActions.changeOpenAlertDelete(true));
    }
  };

  const handleSign = async () => {
    handleClose();
    const response = await dispatch(handleGetEstimate(selectId)).unwrap();
    setEditData(response);
    setOpenSendDocumentDialog(true);
  };

  const handleRevoke = async () => {
    handleClose();
    try {
      const response = await dispatch(handleGetEstimate(selectId)).unwrap();
      await dispatch(handleRevokeDocument(String(response?.documentFile?.id))).unwrap();
      dispatch(estimateActions.changeNeedRefreshData(true));
      notify('success', t('revokeSuccess', { ns: 'admin/common' }));
    } catch (e) {
      catchError(e, (message) => {
        notify('error', message);
      });
    }
  };
  const handleChangePage = (newPage: number) => {
    dispatch(
      estimateActions.setFilterList({
        page: newPage,
      })
    );
  };

  const handleRequestSort = (sortBy: Array<{ orderBy: string; order: SortDirection }>) => {
    dispatch(
      estimateActions.setFilterList({
        sort: sortBy.map((item) => ({
          field: item.orderBy,
          type: item.order,
        })),
      })
    );
  };

  const handleClickAddEstimate = (event: React.MouseEvent<HTMLElement>) => {
    setAddEstimate(event.currentTarget);
  };

  const handleCloseAddEstimate = () => {
    setAddEstimate(null);
  };

  const handleMenuItemClick = (option: string) => {
    if (option === 'createEstimate') {
      setEditData(initValues);
      setCreateEstimateDialogMode(DIALOG_MODE_CREATE);
      setOpenCreateEstimateDialog(true);
    } else {
      setOpenCreateFromTemplate(true);
    }
    handleCloseAddEstimate();
  };

  const handleSetOpen = async (open: boolean, needRefreshData?: boolean) => {
    setOpenCreateEstimateDialog(open);
    // if (needRefreshData) dispatch(jobActions.changeNeedRefreshData(true));
  };
  //#endregion Handle Function

  const handleOpenCreateFromTemplate = (value: boolean) => {
    setOpenCreateFromTemplate(value);
  };

  const handleSetTemplateId = async (value: Array<number>) => {
    setOpenCreateFromTemplate(false);
    try {
      const responses = await Promise.all(value.map((id) => dispatch(handleGetEstimateTemplate(String(id))).unwrap()));

      const combinedData: IResponseGetEstimate = responses.reduce(
        (acc, response) => {
          return {
            id: 0,
            total: acc.total + response.total,
            subtotal: acc.subtotal + response.subtotal,
            tax: acc.tax + response.tax,

            estimateItems: [...acc.estimateItems, ...response.estimateItems],
            estimateFiles: [...acc.estimateFiles, ...response.estimateFiles],
          };
        },
        {
          id: 0,
          total: 0,
          subtotal: 0,
          tax: 0,
          estimateItems: [],
          estimateFiles: [],
        } as IResponseGetEstimate
      );

      setEditData(combinedData);
      setCreateEstimateDialogMode(DIALOG_MODE_CREATE);
      setOpenCreateEstimateDialog(true);
    } catch (e) {
      handleApiError(e);
    }
  };
  const closeAlertDeleteModal = useCallback(() => {
    dispatch(estimateActions.changeMenuSelectId(undefined));
    dispatch(estimateActions.changeOpenAlertDelete(false));
  }, [dispatch]);

  const openAlertDeleteModal = useCallback(() => {
    dispatch(estimateActions.changeOpenAlertDelete(true));
  }, [dispatch]);

  const onDeleteSelectedEstimates = useCallback(async () => {
    let payloadDeleteEstimates: IPayloadDeleteEstimates =
      menuSelectedId !== undefined ? { ids: [menuSelectedId] } : { ids: [...selectedIds] };
    try {
      await estimateApi.deleteEstimate(payloadDeleteEstimates);
      notify('success', t('deleteSuccessMessage'));
      dispatch(estimateActions.changeNeedRefreshData(true));
      dispatch(estimateActions.changeOpenAlertDelete(false));
    } catch (e: unknown) {
      catchError(e, (message) => {
        notify('error', message);
      });
    }
  }, [dispatch, menuSelectedId, selectedIds, t]);

  return (
    <Box className={cx('estimate-list-container')}>
      <Box className='flex justify-between'>
        <Box className='text-[20px] font-[400]'>{t('estimate.list')}</Box>
        <Box>
          <Button
            className={cx(
              { 'visibility: visible': selectedIds.length > 0 },
              { 'visibility: hidden': selectedIds.length <= 0 }
            )}
            onClick={openAlertDeleteModal}
            variant='contained'
            color='error'
            disableElevation
            startIcon={<TrashIcon className='w-[17px] h-[17px]' stroke='white' />}
          >
            {t('delete')}
          </Button>
          {permissions[PERMISSIONS.projectEstimate_create] && (
            <Button
              className='w-[170px] h-[36px] px-[7px] py-[9.5px] text-[14px]/[16.8px] font-medium rounded-[8px] ml-[10px]'
              variant='contained'
              disableElevation
              onClick={handleClickAddEstimate}
              endIcon={<ArrowDownMenuIcon className={cx('custom-arrow-icon')} />}
            >
              {t('estimate.addNewEstimate')}
            </Button>
          )}
          <Box className='h-[36px] '></Box>

          <Menu anchorEl={addEstimate} open={Boolean(addEstimate)} onClose={handleCloseAddEstimate}>
            <MenuItem onClick={() => handleMenuItemClick('createEstimate')} className={cx('menu-item-add-estimate')}>
              {t('estimate.createEstimate')}
            </MenuItem>
            <MenuItem onClick={() => handleMenuItemClick('useFromEstimate')} className={cx('menu-item-add-estimate')}>
              {t('estimate.useFromTemplate')}
            </MenuItem>
          </Menu>
        </Box>
      </Box>
      <BaseTable<IEstimateList>
        data={estimateList}
        setSelectedIds={setSelectedIds}
        headCells={headCells}
        onChangePage={handleChangePage}
        onRequestSort={handleRequestSort}
        page={currentPage}
        sort={filterList.sort}
        totalPages={totalPages}
        renderCell={(estimate: IEstimateList) => {
          return (
            <>
              <TableCell component='th' scope='row' padding='none'>
                <Box className={cx('estimate-name-container')}>
                  <Box className={cx('estimateName')}>{estimate.clientName}</Box>
                </Box>
              </TableCell>
              <TableCell>
                <Box className={cx('created-by-container')}>
                  <Box className={cx('name')}>
                    <Box> {formatCurrency(estimate.totalAmount)}</Box>
                  </Box>
                </Box>
              </TableCell>
              <TableCell>
                <Box className={cx('estimate-container')}>
                  <Box className={cx('created-by-container')}>
                    <Box> {estimate?.createdAt ? dayjs(estimate.createdAt).format('DD MMM, YYYY') : ''}</Box>
                  </Box>
                </Box>
              </TableCell>
              <TableCell>
                <Box className={cx('deadline-container')}>
                  <IconButton
                    onClick={(event) => {
                      openOptionMenu(event, estimate.id, estimate.signDocumentId, estimate.signStatus);
                    }}
                  >
                    <MenuVerticalIcon className='w-[19px] h-[19px]' />
                  </IconButton>
                </Box>
              </TableCell>
            </>
          );
        }}
      />
      <MenuOption
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        handleEditItem={handleEditItem}
        handleDeleteItem={handleDeleteItem}
        enableSign={permissions[PERMISSIONS.projectEstimate_esign] && enableSign}
        handleSign={handleSign}
        enableRevoke={permissions[PERMISSIONS.projectEstimate_esign] && enableRevoke}
        handleRevoke={handleRevoke}
        enableEdit={permissions[PERMISSIONS.projectEstimate_edit]}
        enableDelete={permissions[PERMISSIONS.projectEstimate_delete]}
      />

      <ModalAlertDelete
        open={openAlertDelete}
        description={t('estimate.alertDescription')}
        onCancel={closeAlertDeleteModal}
        onConfirm={onDeleteSelectedEstimates}
      />
      <CreateEstimateDialog
        jobId={props.id}
        data={props.data}
        open={openCreateEstimateDialog}
        setOpen={handleSetOpen}
        mode={createEstimateDialogMode}
        editData={editData}
        updateData={props.updateData}
      />
      <CreateFromTemplateDialog
        open={openCreateFromTemplate}
        setOpen={handleOpenCreateFromTemplate}
        setTemplateId={handleSetTemplateId}
      />
      <SendDocumentDialog
        open={openSenDocumentDialog}
        id={editData?.id}
        documentFile={editData?.documentFile}
        setOpen={(value, success) => {
          setOpenSendDocumentDialog(value);
          success && dispatch(estimateActions.changeNeedRefreshData(success));
        }}
        jobId={props.data?.id}
        customerEmail={props.data?.customer.email}
      />
    </Box>
  );
}

export default memo(EstimateList);
